/**
 * Whether a property is currently rented, from `rental_listings`.
 *
 * A property counts as rented when it has at least one listing in the `rented`
 * status. The cash-flow view uses this to label operating months "rented" vs
 * "vacant" — an owned, stabilized property with no rented listing is vacant,
 * not rented.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";

interface RentalListingStatusRow {
  status: string | null;
}

export function usePropertyRentalStatus(pipelinePropertyId?: string | null) {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  const query = useQuery({
    queryKey: ["property-rental-status", pipelinePropertyId] as const,
    enabled: Boolean(workspaceId && pipelinePropertyId),
    queryFn: async (): Promise<RentalListingStatusRow[]> => {
      if (!workspaceId || !pipelinePropertyId) return [];
      const { data, error } = await supabase
        .from("rental_listings")
        .select("status")
        .eq("workspace_id", workspaceId)
        .eq("pipeline_property_id", pipelinePropertyId)
        .eq("status", "rented");
      if (error) throw error;
      return (data ?? []) as RentalListingStatusRow[];
    },
  });

  const isRented = useMemo(
    () => (query.data ?? []).some((r) => r.status === "rented"),
    [query.data],
  );

  return { isRented, isLoading: query.isLoading };
}
